import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  useAppKit,
  useAppKitAccount,
  useDisconnect,
  useWalletInfo,
} from '@reown/appkit/react';
import { useWalletClient } from 'wagmi';

import { uid } from 'modules/common/utils/uid';

import { DEFAULT_CONNECTION_KEY, EConnectionType } from '../const';
import {
  resetConnectionState,
  setConnectionType,
} from '../store/connectionSlice';
import { IUseRawConnectionData } from '../types';
import { useConnectWalletScreen } from './useConnectWalletScreen';

interface IUseReownConnectionData extends IUseRawConnectionData {
  isConnecting: boolean;
}

export const SELECTED_CONNECTION_TYPE = EConnectionType.EthWallet;

export function useReownConnection(): IUseReownConnectionData {
  const dispatch = useDispatch();
  const [isConnectRequested, setConnectRequested] = useState(false);
  const { onOpenConnectWallet } = useConnectWalletScreen();
  const { isConnected, address, status } = useAppKitAccount();

  const { open: openConnectModal } = useAppKit();
  const { disconnect } = useDisconnect();

  const { data: walletClient } = useWalletClient();
  const getWalletKey = useCallback(() => {
    if (!isConnected) {
      return DEFAULT_CONNECTION_KEY;
    }

    return walletClient?.uid || `reown-id-${uid()}`;
  }, [isConnected, walletClient]);

  const connect = useCallback(async () => {
    if (isConnected) {
      onOpenConnectWallet();
    } else {
      setConnectRequested(true);
      void openConnectModal();
    }

    dispatch(setConnectionType(SELECTED_CONNECTION_TYPE));
    return Promise.resolve();
  }, [dispatch, isConnected, onOpenConnectWallet, openConnectModal]);

  useEffect(() => {
    if (isConnectRequested && isConnected) {
      setConnectRequested(false);
      onOpenConnectWallet();
    }
  }, [isConnected, isConnectRequested, onOpenConnectWallet]);

  const info = useWalletInfo();
  const walletIcon = info?.walletInfo?.icon ?? null;

  const disconnectRef = useRef(disconnect);
  disconnectRef.current = disconnect;
  const handleDisconnect = useCallback(async () => {
    await disconnectRef.current();

    dispatch(resetConnectionState(SELECTED_CONNECTION_TYPE));
  }, [dispatch]);

  return {
    accountAddress: address ?? null,
    chainId: walletClient?.chain.id ?? null,
    isConnected,
    getWalletKey,
    connect,
    disconnect: handleDisconnect,
    walletIcon,
    isConnecting: status === 'connecting',
  };
}
